import React, { useState } from 'react';
import PageActions from '../../actions/page';
import Toggle from '../Toggle';
import translations from '../../translations';
import { Modal } from 'react-bootstrap';
import { isNullOrEmpty } from '../../utils/core';

export const isInvalidName = (inputRef) => {
    const inputElement = inputRef.current;
    const isEmpty = isNullOrEmpty(inputElement.value);
    return isEmpty || inputElement.validity.patternMismatch;
};
/**
 * Allows you to change the name, summary and label of a page
 * The config appears in a modal if the isNew value is true,
 * which is used for when a page is first created and requires
 * a page name to be given by the user
 *
 * @param {Object} page the current page we wish to update
 * @param {String} pageName the name of the current page
 * @param {Boolean} isNew indicator as to whether this page has just been created
 * @param {React.Component} container the component for the modal to appear within
 * @param {Function} closeTab the function to close an Editor tab
 */
export const PageConfig = ({ page, pageName, saveHiddenInputs, isNew, container, closeTab }) => {

    const [invalidName, setInvalidName] = useState(false);
    const [canSaveHiddenInputs, toggleSaveHiddenInputsOption] = useState(saveHiddenInputs);

    const nameInput = React.createRef();
    const labelInput = React.createRef();
    const summaryInput = React.createRef();

    // Because the Draw2 service outputs booleans as strings...
    const isTrueOrFalse = canSaveHiddenInputs.toUpperCase() === 'TRUE';

    const onSave = () => {
        PageActions.editPageData('developerName', nameInput.current.value, page.id.value);
        PageActions.editPageData('label', isNew ? page.label.value : labelInput.current.value, page.id.value);
        PageActions.editPageData('developerSummary', summaryInput.current.value, page.id.value);
        PageActions.editPageData('saveHiddenInputs', canSaveHiddenInputs, page.id.value);
        PageActions.setEditingType(null, page.id.value);
    };

    const onCancel = () => isNew ?
        closeTab() :
        PageActions.setEditingType(null, page.id.value);

    const configFormFooter = (
        <>
            <button className="btn btn-default btn-outline page-config-cancel" onClick={onCancel}>Cancel</button>
            <button
                className="btn btn-primary page-config-save"
                onClick={isNew ? null : onSave}
                type={isNew ? 'submit' : 'input'}
                disabled={invalidName}
            >
                Save
            </button>
        </>
    );

    const nameFormGroup = (
        <div className="form-group">
            <label className="control-label">
                Name
                {isNew ? <span className="input-required"> *</span> : null}
            </label>
            <input
                className="form-control"
                type="text"
                ref={nameInput}
                defaultValue={pageName}
                required={isNew}
                // disallow leading, trailing, multiple and only spaces in page names
                pattern="^[\S]+(\s[\S]+)*$"
                onChange={() => setInvalidName(isInvalidName(nameInput))}
            />
            <span
                className={`help-block ${invalidName && 'error-state'}`}
            >
                Page names cannot be blank, or contain leading, trailing, or multiple space characters.
            </span>
        </div>
    );

    const labelFormGroup = (
        <div className="form-group">
            <label className="control-label">Label</label>
            <input className="form-control" type="text" ref={labelInput} defaultValue={page.label.value} />
            <span className="help-block">The label will be displayed at the top of the page as a header</span>
        </div>
    );

    const summaryFormGroup = (
        <div className="form-group">
            <label className="control-label">Summary</label>
            <textarea className="form-control" type="textarea" ref={summaryInput} defaultValue={page.developerSummary.value} rows={3} />
        </div>
    );

    const configFormBody = (
        <div>
            {nameFormGroup}
            {isNew ? null : labelFormGroup}
            {summaryFormGroup}
            <div className="form-group">
                <Toggle
                    isOn={isTrueOrFalse}
                    onChange={() => toggleSaveHiddenInputsOption(String(!isTrueOrFalse))}
                />
                <label>{translations.PAGE_CONFIG_save_hidden_inputs_label}</label>   
                <span className="help-block">{translations.PAGE_CONFIG_save_hidden_inputs_help_text}</span>             
            </div>
            {isNew ? null : <div className="page-config-footer">{configFormFooter}</div>}
        </div>
    );

    return isNew ? (
        <Modal show container={container}>
            <Modal.Header>
                <Modal.Title>Create New Page</Modal.Title>
            </Modal.Header>
            <form
                name="CreateNewPageForm"
                onSubmit={(event) => {
                    onSave();
                    event.preventDefault();
                }}
            >
                <Modal.Body>
                    {configFormBody}
                </Modal.Body>
                <Modal.Footer>
                    {configFormFooter}
                </Modal.Footer>
            </form>
        </Modal>
    ) : (
        <div className="page-config" key="page-config">
            {configFormBody}
        </div>
    );
};

export default PageConfig;
